/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { ComponentProps, MouseEvent, useMemo } from 'react';

import { Button, Typography } from '@mui/material';

import { SelectItemCatalogueButton } from './components/SelectItemCatalogueButton/SelectItemCatalogueButton';
import { SelectItemCatalogueIcon } from './components/SelectItemCatalogueIcon/SelectItemCatalogueIcon';
import { SelectItemCatalogueMember } from './components/SelectItemCatalogueMember/SelectItemCatalogueMember';
import { useSelectItemCatalogueStyles } from './SelectItemCatalogue.styles';

type SelectItemCatalogueProps<V, C> = {
  options: V[];
  selected: C;
  className?: string;
  itemClassName?: string;
  title?: string;
  getOptionLabel?: (option: V) => string;
  getOptionSelected?: (option: V, selected: C) => boolean;
  isDisabled?: boolean;
  onItemClick: (event: React.MouseEvent<HTMLButtonElement>, value: V) => any;
  iconType?: 'arrow' | 'check' | 'warning';
  noOptionsText?: string | React.ReactNode;
  buttonProps?: ComponentProps<typeof Button>;
  renderOption?: (params: {
    key: string | number;
    className?: string;
    isActive: boolean;
    isDisabled?: boolean;
    optionLabel: string;
    option: V;
    icon: React.ReactElement<any>;
    selected: C;
    buttonProps?: ComponentProps<typeof Button>;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => any;
  }) => React.ReactElement<any> | false;
};

export function SelectItemCatalogue<V, C = V>(props: SelectItemCatalogueProps<V, C>) {
  const {
    className = '',
    title,
    itemClassName = '',
    options,
    getOptionLabel = <V,>(option: V) => `${option}`,
    getOptionSelected = <V, C extends V = V>(option: V, selected: C) => option === selected,
    selected,
    onItemClick,
    iconType = 'arrow',
    noOptionsText,
    isDisabled,
    buttonProps = {},
    renderOption = () => false,
  } = props;
  const classes = useSelectItemCatalogueStyles();

  const optionsList = useMemo(
    () =>
      options.map((option, key) => {
        const icon = <SelectItemCatalogueIcon type={iconType} />;
        const isActive = getOptionSelected(option, selected);
        const optionLabel = getOptionLabel(option);

        const handleClick = (event: MouseEvent<HTMLButtonElement>) => onItemClick(event, option);

        const props = {
          buttonProps,
          className: itemClassName,
          onClick: handleClick,
          isActive,
          icon,
          optionLabel,
        };

        const renderOptionsResult = renderOption({
          ...props,
          key,
          isDisabled,
          option,
          selected,
        });

        return (
          <SelectItemCatalogueMember
            {...props}
            key={key}
            disabled={isDisabled}
            autoFocus={!isActive && key === 0}
            fallback={renderOptionsResult}
          />
        );
      }),
    [
      buttonProps,
      getOptionLabel,
      getOptionSelected,
      iconType,
      isDisabled,
      itemClassName,
      onItemClick,
      options,
      renderOption,
      selected,
    ],
  );

  return (
    <div className={cx(classes.root, className)}>
      {title && (
        <Typography classes={{ h4: classes.title }} variant="h4" component="h4">
          {title}
        </Typography>
      )}
      {options.length ? (
        <div className={classes.list}>{optionsList}</div>
      ) : (
        noOptionsText && <div className={classes.noOptionsText}>{noOptionsText}</div>
      )}
    </div>
  );
}

SelectItemCatalogue.Button = SelectItemCatalogueButton;
SelectItemCatalogue.Icon = SelectItemCatalogueIcon;
